import React from 'react';

import { Box, Typography, alpha } from '@mui/material';
import type { Theme } from '@mui/material';
import { MoreHorizontal } from 'lucide-react';

import { font } from 'settings/tokens';
import type { CertificateActionItem } from 'components/pages/certificates/certificateActionItems';

import { stateCardSurfaceSx } from './stateCardSurface';

/**
 * The certificate's discretionary actions, as a ROW OF EQUAL TILES.
 *
 * ## Why a row of peers, and not the single doorway this replaces
 *
 * The control here was one card labelled `Actions` that opened a sheet, and its
 * own comment argued the case: there is no primary among Gift / Sell / Report,
 * so promoting one would teach a hierarchy that does not exist, and a single
 * trigger scales to N actions with no width pressure.
 *
 * The first half of that is right and is exactly why this is a row of IDENTICAL
 * tiles — no size, colour or position says one of these matters more. What the
 * doorway got wrong is the conclusion: it treated "there is no primary" as a
 * reason to promote NONE of them, which routes every action, including the ones
 * you came to the page to take, through a tap that reveals nothing. A row of
 * peers renders the peer relationship directly. It is the iOS contact-card
 * idiom (Message / Call / Video / Mail) — four equal glyph-and-verb targets, no
 * ranking implied, all of them one tap away.
 *
 * The scaling objection is answered by the cap rather than by hiding everything:
 * past four actions the last tile becomes `More` and opens the same sheet, which
 * is why that sheet is still here. It is the overflow path now, not dead code.
 *
 * ## Each tile is its own CARD, not four cells inside one
 *
 * Both were viable and the alternative is worth stating, because at four tiles
 * this is four hairlines and four shadows where one container would be one.
 *
 * Tiles won on two counts. First, this page's surface language is already
 * "a white 12px card with a hairline is ONE tap target" — the state cards, the
 * Details block, the doorway being replaced. Four targets inside one card breaks
 * that invariant on the one page that establishes it. Second, and decisively,
 * `Sell` is disabled: a disabled CELL means dimming a region inside a live card,
 * which reads as a card that failed to render, whereas a disabled TILE is simply
 * an inert card among live ones — a state the page already has vocabulary for.
 *
 * ## It WRAPS at three per row — it does not scroll, and it does not shrink
 *
 * `ImageCarousel` sits directly above this and is swipeable, so a second
 * horizontal gesture in the same column would compete with it — you would swipe
 * the tiles and get the photograph, or the reverse, depending on a few pixels of
 * touch slop. Scrolling is also the worse failure on its own terms: an action
 * past the right edge has no affordance saying it exists, and "what can I do
 * with this item" is the entire question this row answers.
 *
 * The alternative to scrolling is to divide the row by however many actions
 * there are, which is what this did — `flex: 1 1 0` across a single line. That
 * makes the TILE a function of the action COUNT: the same Gift tile was 106px
 * wide with three actions and 76.75px with four, so adding `Verify` silently
 * reshaped the two beside it. A fixed three-up grid holds the tile constant and
 * lets the row grow downward instead, which is also what keeps them square.
 */

/**
 * Three per row, and the tile is a SQUARE.
 *
 * The record column measures 343px at 375 (375 minus the section Stack's 16px
 * gutters), so three tiles with two 12px gaps are (343 - 24) / 3 = 106.33px —
 * and `aspectRatio: 1` makes that the height too. That is the width the row
 * already had at three actions, now held whatever the count.
 *
 * Four would be 76.75px, which is where the old single-line layout ended up and
 * is one word at 14px with nothing spare; the `Coming soon` sub-label alone
 * measures 65px.
 */
const TILES_PER_ROW = 3;

/**
 * Two full rows. Past six the LAST slot becomes `More` and opens the sheet.
 *
 * Unlike the old cap this is not a width constraint — wrapping has no width
 * pressure — it is a HEIGHT one: a third row of 106px tiles pushes the product
 * details below the fold on a 812px screen to show actions that are, by then,
 * rare enough to belong in a list.
 */
const MAX_TILES = 6;

/**
 * Bigger than the 20 the shared defs declare (that size is tuned for the sheet's
 * rows, where the glyph sits beside 16px text). Here the glyph is the tile's
 * primary mark and the label is subordinate to it, so the relationship inverts.
 *
 * 26, up from an initial 22 that read undersized against a 106px tile: the glyph
 * is what the eye lands on and the verb only confirms it, so it has to carry more
 * of the tile than a 14px label does. Bounded ABOVE by the four-tile case, where
 * the usable width inside the padding is 68.75px — the glyph has room, but the
 * `Coming soon` sub-label measures 65.0px at 11px and is the real constraint on
 * how much vertical and horizontal weight this row can take. Anything that grows
 * the tile's content box has to be re-checked against that number, not this one.
 */
const TILE_ICON_SIZE = 26;

/**
 * DESKTOP (md+) IS A DIFFERENT CONTROL, not the same one scaled.
 *
 * The tile is `flex: 0 0 calc((100% - 24px) / 3)` with `aspectRatio: 1`, which is
 * right on the device this app is for: at 375 the record column is 343px, so a
 * tile is 106px square — a thumb target. The square is a function of the COLUMN,
 * though, and on desktop the record column is the 7/12 track of a 1232px grid,
 * about 662px. The same rule then yields a **212px square**, so three verbs sit
 * in a 212px-tall band of mostly empty card. Nothing was scaled; the container
 * changed and the rule followed it.
 *
 * This matters more since VRG-778 shipped public share links: a recipient opens
 * a shared certificate on whatever they are using, so desktop is no longer only
 * the owner's incidental second screen.
 *
 * So at md+ the tile becomes the ordinary horizontal button it should have been
 * there all along — glyph left, verb beside it, a fixed height that does not
 * track the column. `MAX_TILES`, the wrap and the `More` overflow are untouched;
 * only the tile's own box changes.
 *
 * ⚠️ MOBILE IS DELIBERATELY UNTOUCHED (working-conventions §37). Every rule below
 * is inside a `breakpoints.up('md')` block, so the phone keeps the square grid.
 */
const DESKTOP_TILE_ICON_SIZE = 20;

/**
 * 56px. Tall enough for the two-line `Coming soon` case — 14px label at 1.2
 * (16.8) plus 11px description at 1.2 (13.2) plus the 4px between them is 34px,
 * inside 56 minus 2x12 padding — so an inert tile and a live one are the same
 * height and the row does not step.
 */
const DESKTOP_TILE_MIN_HEIGHT = 56;

/**
 * 12px, both axes. Stated in px rather than as a `spacing` factor because the
 * tile's flex-basis has to subtract it arithmetically — and because `apps/app`
 * runs on MUI's 8px base while the rest of the design system runs on 4
 * (working-conventions §29), so a factor here would mean one number that reads
 * as 12 and renders as 24.
 */
const TILE_GAP_PX = 12;

interface ActionTileRowProps {
  /**
   * The SAME array the overflow sheet renders. Not a tile-specific list — see
   * `certificateActionItems`; one source is what stops the row and the sheet
   * offering different actions.
   */
  items: CertificateActionItem[];
  /** Opens the overflow sheet. Only ever called from the `More` tile. */
  onOverflow: () => void;
}

type TileProps = {
  icon: React.ReactNode;
  label: string;
  /** Why the tile is inert. Rendered, not hidden behind a hover — see below. */
  description?: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
  ariaLabel?: string;
};

const Tile = ({ icon, label, description, disabled, onClick, ariaLabel }: TileProps) => (
  <Box
    component='button'
    type='button'
    disabled={disabled}
    onClick={onClick}
    aria-label={ariaLabel}
    sx={(theme: Theme) => ({
      // The page's card surface, so a tile is a member of the same family as the
      // state cards and Details rather than a new kind of object.
      ...stateCardSurfaceSx,
      // …minus the card's own block spacing: the ROW owns the margin below it and
      // a tile's padding is its own (a card's 16px would leave a 4-tile row with
      // ~45px of usable width). Both are the same CSS property as the value they
      // replace, so the later declaration simply wins.
      mb: 0,
      // 8px of side padding, up from 4: at a fixed 106px the tile is no longer
      // competing for width with a fourth sibling, so the label does not need
      // the extra 8px of content box it was being given.
      padding: '12px 8px',

      // `<button>`, not a `role='button'` Box. The control this replaces hand-rolled
      // Enter/Space handling; a real button brings that, focus-visible and — the
      // reason it matters here — genuine `disabled` semantics, so `Sell` is skipped
      // by tab order and announced as unavailable rather than merely looking it.
      appearance: 'none',
      fontFamily: 'inherit',
      textAlign: 'center',

      // A fixed third of the row, NOT `flex: 1 1 0`. The basis is computed from
      // the container so it holds at any width, and `grow: 0` is what stops a
      // trailing row of one or two tiles stretching to fill the line — the
      // fourth action lands under the first, at the first's size.
      flex: `0 0 calc((100% - ${(TILES_PER_ROW - 1) * TILE_GAP_PX}px) / ${TILES_PER_ROW})`,
      // Square, so the row reads as a grid of equal targets rather than a strip
      // whose height happens to be whatever the tallest label produced.
      aspectRatio: '1 / 1',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.75,

      // Muted content rather than a blanket `opacity`, for the reason `ActionMenu`
      // records at its own disabled rows: fading the whole tile drags the line
      // explaining WHY it is inert down with it, and that line is the only thing on
      // a disabled tile worth reading. The shadow goes instead — a tile that cannot
      // be pressed should not read as lifted off the page.
      color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
      ...(disabled
        ? { boxShadow: 'none', cursor: 'not-allowed' }
        : {
            cursor: 'pointer',
            transition: 'background-color 150ms ease',
            '&:hover': { backgroundColor: theme.palette.grey[50] },
          }),
      '&:focus-visible': {
        outline: 'none',
        boxShadow: `0 0 0 3px ${alpha(theme.palette.text.primary, 0.12)}`,
      },

      // See DESKTOP_TILE_ICON_SIZE above for why this is a different box rather
      // than the same one at another size.
      [theme.breakpoints.up('md')]: {
        // The square is what tied the tile's HEIGHT to the column's width. Drop
        // it and the height is the content's, floored by DESKTOP_TILE_MIN_HEIGHT.
        aspectRatio: 'auto',
        minHeight: `${DESKTOP_TILE_MIN_HEIGHT}px`,
        flexDirection: 'row',
        justifyContent: 'flex-start',
        textAlign: 'left',
        padding: '12px 16px',
        // 12px. A NUMBER, not a `spacing` factor: `apps/app` runs on MUI's 8px
        // base while the rest of the design system runs on 4 (§29), so a factor
        // here would read as 12 and render as 24 — the same trap TILE_GAP_PX
        // records one block up.
        gap: '12px',
      },

      // lucide bakes `width`/`height` ATTRIBUTES at clone time (`size:
      // TILE_ICON_SIZE` below), so the desktop size cannot come from a prop
      // without a second clone. CSS wins over the presentation attributes.
      '& svg': {
        [theme.breakpoints.up('md')]: {
          width: DESKTOP_TILE_ICON_SIZE,
          height: DESKTOP_TILE_ICON_SIZE,
        },
      },
    })}
  >
    <Box sx={{ display: 'flex', color: 'inherit', flexShrink: 0 }}>{icon}</Box>
    {/*
      The verb and its reason as ONE block, so the desktop row is glyph | text
      rather than glyph | verb | reason laid out in a line.

      On a phone this changes NOTHING: the parent is a centred column with
      `gap: 0.75`, and this wrapper is a centred column with the same gap, so
      three children at 6px apart and two children one of which holds two at 6px
      apart render identically. Verified by measuring, not by inspection.
    */}
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.75,
        minWidth: 0,
        [theme.breakpoints.up('md')]: {
          alignItems: 'flex-start',
          // Tighter than the mobile 6px: the two lines are now a label and its
          // own sub-label in a left-aligned block, which reads as one unit, not
          // as two stacked tile captions.
          gap: 0.5,
        },
      })}
    >
    <Typography
      sx={{
        // `font.label` (14px) — the app's label size, measured to fit `Report` and
        // an eight-character verb at the four-tile width. Deliberately NOT
        // truncated: a clipped verb loses the whole message (§49's argument for
        // headers applies to a label that IS the control). If a word ever overruns
        // it wraps, and because the tiles stretch to a common height the row simply
        // gets taller rather than misaligning.
        fontSize: `${font.label}px`,
        lineHeight: 1.2,
        fontWeight: 500,
        color: 'inherit',
      }}
    >
      {label}
    </Typography>
    {description && (
      /*
        Rendered on the tile, not put in a `tooltip`. This app is reached by
        TAPPING AN NFC TAG WITH A PHONE (§37), where a hover affordance does not
        exist — so a tooltip-only reason is no reason at all for most users, which
        is the same call `ActionMenuItem.description` documents. §46's forcing
        function (a disabled control must carry a readable reason) is satisfied
        here or nowhere: the sheet that carries the same sentence is behind a
        `More` tile that does not exist at three actions.

        `text.secondary`, so it stays legible while the label above it is muted —
        again matching the sheet, where the same inversion is deliberate.
      */
      <Typography
        sx={{ fontSize: '11px', lineHeight: 1.2, color: 'text.secondary' }}
      >
        {description}
      </Typography>
    )}
    </Box>
  </Box>
);

export const ActionTileRow = ({ items, onOverflow }: ActionTileRowProps) => {
  // §46: record state HIDES. An action the certificate's status rules out is gone
  // from the row entirely and the survivors take its width, because every tile is
  // `flex: 1 1 0` — three tiles are three equal thirds, two are halves. There is
  // no per-count sizing to keep in step.
  const visible = items.filter(item => item.visible !== false);

  // A row of zero tiles must not render — the same gate the doorway had, and for
  // the same reason a trigger onto an empty sheet was worse than no trigger.
  // The caller also gates on this; both, because a component that can render an
  // empty bordered strip is a component that eventually does.
  if (visible.length === 0) return null;

  /*
   * The cap. Past SIX, the last slot becomes `More` rather than the seventh
   * action, so the grid is never more than two rows tall and the actions that
   * lost their slot are one tap away in the sheet instead of being silently
   * dropped.
   *
   * Which get promoted is `CERTIFICATE_ACTION_DEFS`' order, unchanged:
   * affirmative first, `Report` last. That means `Report` is the first thing to
   * fall into the overflow, which is correct — it is the rare, negative one, and
   * its own definition says position is what carries that de-emphasis.
   *
   * Dead today at four actions, and deliberately built anyway: the defs file says
   * the set is expected to grow (`sell` is already declared, `transfer` is
   * expected), and a grid that silently runs off the bottom of the page is the
   * failure this cannot be allowed to have.
   */
  const overflows = visible.length > MAX_TILES;
  const shown = overflows ? visible.slice(0, MAX_TILES - 1) : visible;

  return (
    <Box
      /* A wrapping flex GRID, not a single `Stack` row — see `TILES_PER_ROW`.
         `flex-start`, so a trailing row of one or two tiles sits under the first
         column rather than spreading across the line; each tile's own basis is
         what fixes its width. */
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        gap: `${TILE_GAP_PX}px`,
        /*
          THE ROW SAT TOO CLOSE TO THE TITLE AND TOO FAR FROM THE SECTION BELOW.
          Measured in the browser (the rendered gap, not the CSS margin — §40:
          line-height adds leading, so the two differ):

              title  -> tiles     19.2px
              tiles  -> DETAILS   43.2px

          2.2x. Proximity reads that as "these buttons belong to the title", and
          they do — but at 19px against a 43px neighbour it reads as crowding
          rather than grouping, which is what prompted this.

          `mt: 1.5` is 12px (apps/app runs on MUI's 8px base, §29 — the same trap
          TILE_GAP_PX records above), taking the top gap to ~31px. That is the
          app's own header rhythm: `PageHeader` puts 32px between a header block
          and its first content. The bottom stays larger, so the order is still
          title > actions > new section — just no longer 2.2 to 1.

          ⚠️ BOTH WIDTHS, deliberately, unlike the tile's own desktop overrides.
          The gaps measured IDENTICALLY at 375 and 1280 (both margins are 16px
          and neither is responsive), so this is not a desktop defect — it is the
          page's spacing everywhere. A gap that differed by breakpoint for one
          relationship would be drift, not a fix.
        */
        mt: 1.5,
        mb: 2,
      }}
    >
      {shown.map(item => (
        <Tile
          key={item.key}
          icon={
            React.isValidElement(item.icon)
              ? React.cloneElement(item.icon as React.ReactElement<{ size?: number }>, {
                  size: TILE_ICON_SIZE,
                })
              : item.icon
          }
          /* The SHORT label. `item.label` — the full "Report lost or stolen" — is
             what the sheet and the bulk bar render; only this surface abbreviates,
             and the abbreviation is declared beside the full string rather than
             computed here (see `tileLabel`). */
          label={item.tileLabel}
          description={item.disabled ? item.description : undefined}
          disabled={item.disabled}
          onClick={item.onClick}
          /* The tile shows one word; a screen reader gets the whole sentence,
             including the reason it is inert. */
          ariaLabel={
            item.disabled && typeof item.description === 'string'
              ? `${item.label} — ${item.description}`
              : item.label
          }
        />
      ))}
      {overflows && (
        <Tile
          icon={<MoreHorizontal size={TILE_ICON_SIZE} strokeWidth={1.5} />}
          label='More'
          onClick={onOverflow}
          ariaLabel={`More actions (${visible.length - shown.length})`}
        />
      )}
    </Box>
  );
};
